// Tooltip component that displays data retrieved from a backend API call.
//
// Features:
// - 1 second delay before showing (enterDelay)
// - 1 second delay before hiding (leaveDelay)
// - Interactive: can hover over tooltip content without it closing
// - Shows spinner while data is loading
// - Shows error message on fetch failure
// - Triggers data fetch on hover (before delay)
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui';
import { makeStyles } from '@material-ui/core/styles';
import * as colors from '@constants/colors';
import DotmapsLoader from '@shared/dotmaps-loader';

const useStyles = makeStyles(() => ({
  tooltip: {
    backgroundColor: colors.overlayTextColor,
    borderRadius: '2px',
    boxShadow: `0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14), 0 3px 5px -1px ${colors.dotmapsBlack20}`,
    width: '21.25rem',
    color: colors.textColor,
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.75rem',
    lineHeight: '1.33',
    padding: 0,
    maxWidth: 'none'
  }
}));

const DataFetchTooltip = ({
  children,
  data,
  error,
  onFetch,
  renderContent,
  renderError
}) => {
  const classes = useStyles();

  // Trigger fetch on mouse enter (before tooltip delay)
  const handleMouseEnter = useCallback(() => {
    if (!data && !error && onFetch) {
      onFetch();
    }
  }, [data, error, onFetch]);

  // Determine tooltip content based on state
  const getTooltipContent = () => {
    if (data) {
      return renderContent();
    }
    if (error) {
      return renderError ? renderError() : <div style={{ padding: '1rem' }}>Error loading data</div>;
    }
    // Loading state
    return (
      <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
        <DotmapsLoader />
      </div>
    );
  };

  return (
    <Tooltip
      title={getTooltipContent()}
      placement="bottom-start"
      enterDelay={1000}
      leaveDelay={1000}
      interactive
      classes={{ tooltip: classes.tooltip }}
    >
      <span onMouseEnter={handleMouseEnter} style={{ cursor: 'pointer' }}>
        {children}
      </span>
    </Tooltip>
  );
};

DataFetchTooltip.propTypes = {
  children: PropTypes.node,
  data: PropTypes.object,
  error: PropTypes.object,
  onFetch: PropTypes.func,
  renderContent: PropTypes.func,
  renderError: PropTypes.func
};

export default DataFetchTooltip;
